import React, { useEffect, useState } from "react";
import { Plus, Play, ChevronRight, Pencil } from "lucide-react";
import { toast } from "sonner";
import { listPolicies } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import PolicyForm from "@/components/PolicyForm";

export default function Policies() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => listPolicies().then((r) => {
    const items = r.items || [];
    setItems(items);
    // Seed selection only when none is set, using functional update to avoid dep.
    setSelected((prev) => prev || items[0] || null);
  }).catch((err) => toast.error(err?.response?.data?.detail || "Failed to load policies"));

  useEffect(() => { load(); }, []);

  const onSaved = (saved) => {
    setItems((prev) => {
      const idx = prev.findIndex((p) => p.id === saved.id);
      if (idx >= 0) { const copy = [...prev]; copy[idx] = saved; return copy; }
      return [...prev, saved];
    });
    setSelected(saved);
  };

  const openNew = () => { setEditing(null); setFormOpen(true); };
  const openEdit = () => { setEditing(selected); setFormOpen(true); };

  return (
    <div className="p-8 md:p-10 space-y-6" data-testid="policies-page">
      <div className="flex items-end justify-between gap-4">
        <div>
          <div className="tracked-caps">Rules engine</div>
          <h2 className="mt-1 font-serif-display text-3xl">Policies</h2>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Active policies that gate extraction, review, and publication. Every policy has scope, version, and approver.
          </p>
        </div>
        <button onClick={openNew} className="inline-flex items-center gap-2 rounded-md bg-brand text-brand-foreground px-3 py-2 text-sm font-medium hover:opacity-90" data-testid="new-policy">
          <Plus className="h-4 w-4" /> New policy
        </button>
      </div>

      <PolicyForm open={formOpen} onOpenChange={setFormOpen} policy={editing} onSaved={onSaved} />

      <div className="grid md:grid-cols-12 gap-5">
        <aside className="md:col-span-5 rounded-lg border border-border bg-card overflow-hidden">
          <ul className="divide-y divide-border">
            {items.map((p) => {
              const active = selected?.id === p.id;
              return (
                <li key={p.id}>
                  <button
                    onClick={() => setSelected(p)}
                    data-testid={`policy-row-${p.id}`}
                    className={`w-full text-left px-5 py-4 flex items-start justify-between gap-3 transition-colors ${active ? "bg-muted" : "hover:bg-muted/50"}`}
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs text-muted-foreground">{p.id}</span>
                        <span className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">{p.version}</span>
                      </div>
                      <div className="mt-1 text-sm font-medium">{p.name}</div>
                      <div className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{p.summary}</div>
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground mt-1" />
                  </button>
                </li>
              );
            })}
          </ul>
        </aside>

        <section className="md:col-span-7 space-y-4">
          {selected && (
            <>
              <div className="rounded-lg border border-border bg-card p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-xs text-muted-foreground">{selected.id}</span>
                    <span className="rounded border border-border bg-muted/40 px-2 py-0.5 font-mono text-[11px] uppercase tracking-wider">{selected.version}</span>
                  </div>
                  <button
                    onClick={openEdit}
                    className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-3 py-1.5 text-xs hover:bg-muted"
                    data-testid="policy-edit"
                  >
                    <Pencil className="h-3.5 w-3.5" /> Edit
                  </button>
                </div>
                <h3 className="mt-2 font-serif-display text-2xl leading-snug">{selected.name}</h3>
                <p className="mt-2 text-foreground/80 leading-relaxed">{selected.summary}</p>

                <div className="mt-6 grid md:grid-cols-3 gap-4 text-sm">
                  <Field k="Scope" v={selected.scope.join(", ")} />
                  <Field k="Domains" v={selected.domains.join(", ")} />
                  <Field k="Approved by" v={selected.approved_by} />
                  <Field k="Approved on" v={<span className="font-mono text-xs">{fmtDate(selected.approved_at)}</span>} />
                  <Field k="Triggered flags" v={<span className="font-mono">{selected.triggered_flags}</span>} />
                </div>
              </div>

              <div className="rounded-lg border border-border bg-card p-6">
                <div className="flex items-center justify-between">
                  <div className="tracked-caps">Simulation</div>
                  <button className="inline-flex items-center gap-1.5 rounded-md bg-brand text-brand-foreground px-3 py-1.5 text-xs hover:opacity-90" data-testid="policy-simulate">
                    <Play className="h-3 w-3" /> Run on last 90 days
                  </button>
                </div>
                <p className="mt-3 text-sm text-muted-foreground">
                  What would happen if this policy applies to the past 90 days of candidate extractions?
                </p>
                <div className="mt-4 grid grid-cols-3 gap-4">
                  <Stat k="Candidates reviewed" v="412" />
                  <Stat k="Would be flagged" v={selected.triggered_flags ? `${selected.triggered_flags * 2}` : "0"} />
                  <Stat k="Would be blocked" v={selected.triggered_flags ? `${Math.round(selected.triggered_flags * 0.3)}` : "0"} />
                </div>
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}

function Field({ k, v }) {
  return (
    <div>
      <div className="tracked-caps">{k}</div>
      <div className="mt-1">{v}</div>
    </div>
  );
}
function Stat({ k, v }) {
  return (
    <div className="rounded-md border border-border p-4 bg-muted/20">
      <div className="tracked-caps">{k}</div>
      <div className="mt-1 font-serif-display text-2xl">{v}</div>
    </div>
  );
}
